import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileCode2, Save, Tag, Text, X } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { FormField, inputClass, selectClass, textareaClass } from "./FormField";
import { diagnosticTypeOptions } from "../data";
import type { StandardDiagnosticCode } from "../types";

type StandardDiagnosticCodeFormProps = {
  labels: {
    fields: Record<"code" | "name" | "type" | "description", string>;
    placeholders: Record<"code" | "name" | "description", string>;
    typePlaceholder: string;
    submit: string;
  };
  cancelHref: string;
  initialValues?: StandardDiagnosticCode;
  onSubmit: (values: Omit<StandardDiagnosticCode, "id">) => void | Promise<void>;
};

export function StandardDiagnosticCodeForm({
  labels,
  cancelHref,
  initialValues,
  onSubmit,
}: StandardDiagnosticCodeFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = {
      code: String(formData.get("code") ?? "").trim(),
      label: String(formData.get("label") ?? "").trim().toUpperCase(),
      type: String(formData.get("type") ?? "").trim(),
      description: String(formData.get("description") ?? "").trim(),
    };

    void (async () => {
      setSaving(true);
      setError(null);
      try {
        await onSubmit(values);
        navigate("/dashboard/acts/standard-diagnostics");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Enregistrement impossible.");
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <p className="mb-4 text-sm text-pink">{error}</p>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label={labels.fields.code} required>
          <div className="relative">
            <FileCode2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              name="code"
              defaultValue={initialValues?.code}
              placeholder={labels.placeholders.code}
              className={`${inputClass} pl-9`}
              required
            />
          </div>
        </FormField>

        <FormField label={labels.fields.type} required>
          <div className="relative">
            <Tag size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <select
              name="type"
              defaultValue={initialValues?.type ?? ""}
              className={`${selectClass} pl-9`}
              required
            >
              {diagnosticTypeOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option.value ? option.label : labels.typePlaceholder}
                </option>
              ))}
            </select>
          </div>
        </FormField>

        <FormField label={labels.fields.name} required className="md:col-span-2">
          <div className="relative">
            <Text size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              name="label"
              defaultValue={initialValues?.label}
              placeholder={labels.placeholders.name}
              className={`${inputClass} pl-9 uppercase`}
              required
            />
          </div>
        </FormField>

        <FormField label={labels.fields.description} className="md:col-span-2">
          <textarea
            name="description"
            defaultValue={initialValues?.description}
            placeholder={labels.placeholders.description}
            className={`${textareaClass} min-h-[96px]`}
          />
        </FormField>
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Link
            to={cancelHref}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <X size={14} />
            {t.safecheck.cancel}
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <Save size={14} />
            {labels.submit}
          </button>
        </div>
      </div>
    </form>
  );
}
